import { Maybe } from '@vimeo/core/types/generic';

import {
  EAgoraMode,
  EAgoraProxyMode,
  TDualStreamConfig,
  TMediaCodec,
  TScreenEncoderPreset,
  TScreenSVCPreset,
} from '../types/agora';
import { getLiveConfigValue } from '../utils/general/configuration';
import { inline } from '../utils/general/lambda';
import { localStorageConfig } from './LocalStorageConfig';

export const liveMediaConfig = {
  DEVICE_NAME_HASH_REGEX: / \([a-f0-9]{2,8}:[a-f0-9]{2,8}\)$/,
  LOCAL_MEDIA: {
    CONSTRAINTS: inline(() => {
      const width: number = getLiveConfigValue(localStorageConfig.MEDIA.STREAM_WIDTH, 1920);
      const height: number = getLiveConfigValue(localStorageConfig.MEDIA.STREAM_HEIGHT, 1080);
      const framerate: number = getLiveConfigValue(localStorageConfig.MEDIA.STREAM_FRAMERATE, 24);
      const aspectRatio: number = getLiveConfigValue(
        localStorageConfig.MEDIA.STREAM_ASPECT_RATIO,
        16 / 9,
      );

      return {
        aspectRatio: { ideal: aspectRatio, max: aspectRatio, min: aspectRatio },
        height: { ideal: height, max: height, min: 240 },
        frameRate: { ideal: framerate, max: 30 },
        width: { ideal: width, max: width, min: 426 },
      };
    }),
    BITRATE_MAX: getLiveConfigValue(localStorageConfig.MEDIA.STREAM_BITRATE_MAX, 3240),
    BITRATE_MIN: getLiveConfigValue(localStorageConfig.MEDIA.STREAM_BITRATE_MIN, 1620),
    /**
     * Preserve motion for local media on degradation to tell that guest is still alive and can participate in event.
     * For some cases 'balanced' would be an option too, but smoothness is in priority.
     */
    OPTIMIZATION_MODE: getLiveConfigValue(localStorageConfig.MEDIA.STREAM_BITRATE_MIN, 'motion') as
      | 'motion'
      | 'detail',
    VIRTUAL_BACKGROUND_OPTIONS: { type: 'blur', blurDegree: 2 },
  },
  LOCAL_AUDIO: {
    DISCONTINUOUS_TRANSMISSION: getLiveConfigValue(
      localStorageConfig.MEDIA.AUDIO_DISCONTINUOUS_TRANSMISSION,
      true,
    ),
    ECHO_CANCELLATION: getLiveConfigValue(localStorageConfig.MEDIA.AUDIO_ECHO_CANCELLATION, true),
    AUTO_GAIN: getLiveConfigValue(localStorageConfig.MEDIA.AUDIO_AUTO_GAIN, true),
    NOISE_SUPPRESSION: getLiveConfigValue(localStorageConfig.MEDIA.AUDIO_NOISE_SUPPRESSION, true),
    /**
     * - Firefox does not support setting the audio encoding rate.
     * - Safari does not support stereo audio.
     * - On Google Chrome, if the audio profile is set to stereo, the AEC, AGC, and ANS options in audioProcessing are automatically disabled.
     */
    ENCODER: {
      BITRATE: getLiveConfigValue(localStorageConfig.MEDIA.AUDIO_BITRATE, 128), // 128 kBit standard for HQ, can be reduced just in case
      SAMPLE_RATE: getLiveConfigValue(localStorageConfig.MEDIA.AUDIO_SAMPLE_RATE, 48000), // 48kHZ standard
      SAMPLE_SIZE: getLiveConfigValue(localStorageConfig.MEDIA.AUDIO_SAMPLE_SIZE, 16), // 16 bit standard
      STEREO: getLiveConfigValue(localStorageConfig.MEDIA.AUDIO_STEREO, false),
    },
  },
  LOCAL_SCREEN: {
    ENCODER_PRESET: getLiveConfigValue(
      localStorageConfig.MEDIA.SCREEN_ENCODER_PRESET,
      '1080p_2',
    ) as TScreenEncoderPreset,
    SVC_PRESET: getLiveConfigValue(localStorageConfig.MEDIA.SCREEN_SVC_PRESET, undefined) as
      | TScreenSVCPreset
      | undefined,
    OPTIMIZATION_MODE: getLiveConfigValue(
      localStorageConfig.MEDIA.SCREEN_OPTIMIZATION_MODE,
      'detail',
    ) as 'motion' | 'detail',
    NONE: 'NONE' as const,
  },
  AGORA: {
    /**
     * Agora logging level.
     * - 0: DEBUG. Output all API logs.
     * - 1: INFO. Output logs of the INFO, WARNING and ERROR level.
     * - 2: WARNING. Output logs of the WARNING and ERROR level.
     * - 3: ERROR. Output logs of the ERROR level.
     * - 4: NONE. Do not output any log.
     */
    LOG_LEVEL: +getLiveConfigValue(localStorageConfig.LIVE.AGORA_LOG_LEVEL, 3),
    /**
     * Whether web audio mixing should be disabled for agora SDK.
     */
    DISABLE_WEB_AUDIO: getLiveConfigValue(localStorageConfig.LIVE.AGORA_DISABLE_WEB_AUDIO, false),
    /**
     * Whether app should upload logs to agora service.
     */
    LOG_UPLOAD: Boolean(getLiveConfigValue(localStorageConfig.LIVE.AGORA_LOG_UPLOAD, true)),
    /**
     * Whether hard mute mode is enabled for video.
     */
    USE_VIDEO_HARD_MUTE: Boolean(
      getLiveConfigValue(localStorageConfig.LIVE.AGORA_VIDEO_HARD_MUTE, true),
    ),
    /**
     * Whether hard mute mode is enabled for video.
     */
    USE_AUDIO_HARD_MUTE: Boolean(
      getLiveConfigValue(localStorageConfig.LIVE.AGORA_AUDIO_HARD_MUTE, false),
    ),
    /**
     * Separator for UIDs in agora to indicate type.
     */
    UID_META_SEPARATOR: '|',
    VOLUME_CHECK_INTERVAL: 100,
    /**
     * Default codec used in browser for encoding.
     */
    CODEC: getLiveConfigValue<TMediaCodec>(localStorageConfig.MEDIA.STREAM_CODEC, 'vp8'),
    /**
     * The SDK differentiates channel profiles and applies different optimization algorithms accordingly.
     * For example, it prioritizes smoothness and low latency for a video call, and prioritizes video quality for a video streaming.
     * Broadcaster connection is always LIVE.
     */
    MEDIA_MODE: getLiveConfigValue<EAgoraMode>(
      localStorageConfig.MEDIA.AGORA_SDK_MEDIA_MODE,
      EAgoraMode.LIVE,
    ),
    SCREEN_MODE: getLiveConfigValue<EAgoraMode>(
      localStorageConfig.MEDIA.AGORA_SDK_SCREEN_MODE,
      EAgoraMode.LIVE,
    ),
    CLOUD_PROXY: getLiveConfigValue<Maybe<EAgoraProxyMode>>(
      localStorageConfig.MEDIA.AGORA_SDK_CLOUD_PROXY,
      null,
    ),
    /**
     * Do not account volume if VOLUME LEVEL less than 0,4%.
     * todo: ask studio about noise in preview (~0.3%).
     */
    VOLUME_CHECK_THRESHOLD: 0.4,
    /**
     * Interval with which to poll remote connections quality to display in lists..
     */
    REMOTE_NETWORK_CHECK_INTERVAL: 2500,
    /**
     * Fallback option.
     * Check agora SDK to get more explanation.
     * 0 - disable fallback automation.
     */
    REMOTE_STREAM_FALLBACK_OPTION: getLiveConfigValue(
      localStorageConfig.MEDIA.REMOTE_STREAM_FALLBACK_OPTION,
      0,
    ),
    ROOM_VOLUME_CHECK_THROTTLE: getLiveConfigValue(
      localStorageConfig.MEDIA.ROOM_VOLUME_CHECK_THROTTLE,
      0,
    ),
    ROOM_VOLUME_CHECK_INTERVAL: getLiveConfigValue(
      localStorageConfig.MEDIA.ROOM_VOLUME_CHECK_INTERVAL,
      250,
    ),
    AUDIO_VOLUME_CHECK_THRESHOLD: getLiveConfigValue(
      localStorageConfig.MEDIA.AUDIO_VOLUME_CHECK_THRESHOLD,
      30,
    ),
  },
  /**
   * Configuration of agora dual stream feature.
   * Sending 2nd low-q stream to use less performance of remote connections.
   */
  DUAL_STREAM: {
    /**
     * Whether dual stream feature is enabled.
     */
    IS_ENABLED: getLiveConfigValue(localStorageConfig.MEDIA.DUAL_STREAM_MEDIA_ENABLED, true),
    CONFIG: {
      height: getLiveConfigValue(localStorageConfig.MEDIA.LOW_STREAM_HEIGHT, 360),
      width: getLiveConfigValue(localStorageConfig.MEDIA.LOW_STREAM_WIDTH, 640),
      framerate: getLiveConfigValue(localStorageConfig.MEDIA.LOW_STREAM_FRAMERATE, 24),
      bitrate: getLiveConfigValue(localStorageConfig.MEDIA.LOW_STREAM_FRAMERATE, 950),
    } as TDualStreamConfig,
    /**
     * Threshold to check whether stream should be in HQ.
     * Allow HQ only if stream is >= 90% of full preview size.
     */
    GFX_ELEMENT_WIDTH_HQ_TOGGLE_THRESHOLD: 90,
    /**
     * Threshold to check whether streams can be shown in HQ.
     * Allow HQ preview only if actual page renderer size is decreased by 2 or less.
     */
    PREVIEW_SCALE_HQ_TOGGLE_THRESHOLD: 640,
    PREVIEW_SCALE_HQ_TOGGLE_THROTTLE: 500,
    /**
     * Room scale breakpoints.
     */
    ROOM_SCALE_BREAKPOINTS: [4, 8, 16],
    /**
     * Presets for bigger rooms to scale down low stream resolution.
     */
    ROOM_SCALE_PRESETS: [
      { height: 360, width: 640 },
      { height: 270, width: 480 },
      { height: 240, width: 426 },
    ],
  },
  PERFORMANCE: {
    /**
     * Interval to check performance stats of remote RTC.
     */
    PERFORMANCE_CHECK_INTERVAL: 1000,
    /**
     * Give some time for remote sources to connect and start sending frames.
     */
    PERFORMANCE_CHECK_OMITTED_TIME: 7_000,
    /**
     * Timeout indicating that stats are outdated and should not be calculated with stats batch.
     */
    PERFORMANCE_REPORT_TIMEOUT: 30_000,
    /**
     * Interval for batched RTC stats reporting.
     */
    PERFORMANCE_REPORT_INTERVAL: 30_000,
    /**
     * Max samples for in-memory storage before sending them for tracking.
     */
    PERFORMANCE_REPORT_SAMPLES_MAX: 15,
    /**
     * Min samples count to respect average of connection.
     */
    PERFORMANCE_REPORT_CONNECTION_SAMPLES_MIN: 5,
    /**
     * Offset used for bufferized array resizing.
     */
    PERFORMANCE_REPORT_SAMPLES_OFFSET: 5,
    /**
     * Size of stream to consider it as large entry.
     * Everything > 540p, consider 360p streams and 540p media as standard, others are exception (hq or issue).
     */
    PERFORMANCE_LARGE_RESOLUTION_THRESHOLD: 960 * 540,
  },
};
